import { Injectable, inject } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';

/**
 * Opens the payment/billing modals reached from the past-due flow.
 *
 * Why the indirection: `PastDueBalanceComponent` is rendered by
 * `ModalContentComponent`, so injecting `ModalContentService` into it directly
 * would close this cycle:
 *
 *   modal-content.service.ts
 *     -> modal-content.component.ts
 *       -> past-due-balance.component.ts
 *         -> modal-content.service.ts        (cycle)
 *
 * The cycle stays dormant until the bundler's module-evaluation order shifts,
 * and then `ModalContentComponent` resolves to `undefined` and
 * `NgbModal.open()` throws "Cannot read properties of undefined (reading
 * 'ɵcmp')". Routing the calls through this service keeps the modal bodies as
 * leaves in the import graph.
 */
@Injectable({ providedIn: 'root' })
export class PaymentMethodModalService {

  private modalService = inject(ModalContentService);

  /** Invoice history. With `unpaidOnly` the modal asks the backend for just the
   *  past-due set, so the customer sees what they owe rather than the full
   *  history they get from the settings route. */
  openInvoiceHistory(options: { unpaidOnly?: boolean } = {}): NgbModalRef {
    return this.modalService.openModal(ModalContentTypes.INVOICE_HISTORY, {
      closeable: true,
      fullScreenOnMobile: true,
      invoiceHistory: { unpaidOnly: !!options.unpaidOnly },
    });
  }

  /** Edit-payment-method modal — the CTA for a past-due customer on this brand,
   *  whose balance is a card charge that failed. */
  openEditPaymentMethod(): NgbModalRef {
    return this.modalService.openModal(ModalContentTypes.PAYMENT_METHOD, {
      title: 'Update Payment Method',
      closeable: true,
      fullScreenOnMobile: true,
    });
  }
}
